import { Link } from 'react-router-dom';
import { Eye, Heart, Pencil, Trash2, Clock } from 'lucide-react';
import { Product, ProductStatus } from '../../types';
import { useTranslation } from '../../context/TranslationContext';
import { translateWithFallback } from '../../utils/translateWithFallback';
import { translateProductCondition } from '../../utils/productConditionLabel';
import { translateCategoryName } from '../../utils/categoryLabel';

interface SellerListingRowProps {
    product: Product;
    onDelete: (id: number) => void;
}

function statusLabel(product: Product, tr: (k: string, fb: string) => string): { text: string; className: string } {
    if (product.isSold || product.status === ProductStatus.Sold) {
        return { text: tr('account.listings.status.sold', 'Sold'), className: 'bg-gray-200 text-gray-700' };
    }
    if (product.status === ProductStatus.PendingReview) {
        return { text: tr('account.listings.status.draft', 'Draft'), className: 'bg-amber-100 text-amber-800' };
    }
    if (product.status === ProductStatus.Archived || product.status === ProductStatus.Rejected) {
        return { text: String(product.status), className: 'bg-red-100 text-red-800' };
    }
    return { text: tr('account.listings.status.active', 'Active'), className: 'bg-emerald-100 text-emerald-800' };
}

function ageRangeFromSpecs(product: Product): string | null {
    const specs = product.specifications || {};
    for (const cat of Object.values(specs)) {
        for (const [name, value] of Object.entries(cat)) {
            if (/age/i.test(name) && value) return value;
        }
    }
    return null;
}

function relativeAge(iso?: string, tr?: (k: string, fb: string) => string): string {
    if (!iso) return '—';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '—';
    const days = Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
    const t = tr ?? ((_k, fb) => fb);
    if (days < 1) return t('account.listings.age.today', 'Today');
    if (days === 1) return t('account.listings.age.day', '1 day ago');
    if (days < 7) return t('account.listings.age.days', '{n} days ago').replace('{n}', String(days));
    if (days < 30) {
        const w = Math.floor(days / 7);
        return w === 1
            ? t('account.listings.age.week', '1 week ago')
            : t('account.listings.age.weeks', '{n} weeks ago').replace('{n}', String(w));
    }
    const m = Math.floor(days / 30);
    return m === 1
        ? t('account.listings.age.month', '1 month ago')
        : t('account.listings.age.months', '{n} months ago').replace('{n}', String(m));
}

const SellerListingRow = ({ product, onDelete }: SellerListingRowProps) => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const thumb = product.imageUrls?.[0];
    const status = statusLabel(product, tr);
    const ageRange = ageRangeFromSpecs(product);
    const category = product.categories?.[0];
    const isSold = !!product.isSold || product.status === ProductStatus.Sold;
    // Sold = order history; keep view, block edit/delete
    const canManage = !isSold;

    return (
        <article className="flex flex-col gap-3 rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--bg-color),white_40%)] p-3 shadow-sm sm:flex-row sm:items-center sm:gap-4 sm:p-4">
            <Link
                to={`/products/${product.id}`}
                className="relative h-24 w-full shrink-0 overflow-hidden rounded-xl bg-[color-mix(in_srgb,var(--accent-color),transparent_80%)] sm:h-20 sm:w-20"
            >
                {thumb ? (
                    <img src={thumb} alt="" className="h-full w-full object-cover" />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs opacity-50">—</div>
                )}
            </Link>

            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                        <Link
                            to={`/products/${product.id}`}
                            className="line-clamp-2 font-semibold text-[var(--text-color)] hover:underline"
                        >
                            {product.title}
                        </Link>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${status.className}`}>
                                {status.text}
                            </span>
                            <span>{translateProductCondition(tr, product.condition)}</span>
                            {category && (
                                <>
                                    <span aria-hidden>·</span>
                                    <span>{translateCategoryName(t, category)}</span>
                                </>
                            )}
                            {ageRange && (
                                <>
                                    <span aria-hidden>·</span>
                                    <span>{ageRange}</span>
                                </>
                            )}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                            <span className="inline-flex items-center gap-1">
                                <Eye className="h-3.5 w-3.5" aria-hidden />
                                {product.viewCount ?? 0} {tr('account.listings.views', 'views')}
                            </span>
                            <span
                                className="inline-flex items-center gap-1"
                                title={tr('account.listings.favsHint', 'Times this listing was saved by buyers')}
                            >
                                <Heart className="h-3.5 w-3.5" aria-hidden />
                                {product.favouriteCount ?? 0}
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" aria-hidden />
                                {relativeAge(product.createdAt, tr)}
                            </span>
                        </div>
                    </div>
                    <p className="shrink-0 text-lg font-bold text-[var(--text-color)]">
                        €{Number(product.price).toFixed(0)}
                    </p>
                </div>
            </div>

            <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-stretch">
                {canManage ? (
                    <>
                        <Link
                            to={`/edit-product/${product.id}`}
                            className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] bg-[var(--bg-color)] px-3 py-2 text-xs font-medium text-[var(--text-color)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_85%)]"
                        >
                            <Pencil className="h-3.5 w-3.5" aria-hidden />
                            {tr('account.listings.edit', 'Edit')}
                        </Link>
                        <button
                            type="button"
                            onClick={() => onDelete(product.id)}
                            className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-100"
                        >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                            {tr('account.listings.delete', 'Delete')}
                        </button>
                    </>
                ) : (
                    <p
                        className="text-center text-[11px] leading-snug text-[color-mix(in_srgb,var(--text-color),transparent_40%)] sm:max-w-[7.5rem]"
                        title={tr(
                            'account.listings.sold.lockedHint',
                            'Sold listings are kept for order history and cannot be edited or deleted.'
                        )}
                    >
                        {tr('account.listings.sold.locked', 'Sold — view only')}
                    </p>
                )}
            </div>
        </article>
    );
};

export default SellerListingRow;
